import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CLI-01. One place the client list is fetched from, so the list screen and anything that later
// needs a client picker read the same cache rather than two copies of the same rows.

/** The shape the API returns, taken from the generated contract rather than restated here. */
export type Client = components['schemas']['ClientView'];

export type NewClient = {
  kind: 'organisation' | 'individual';
  name: string;
  paymentTerms: 'prepay' | 'account';
  accountDays?: number;
  priceGroupId?: string;
  accountManagerId?: string | null;
  contact?: { name: string; email?: string; phone?: string };
};

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const clientsQuery = queryOptions({
  queryKey: ['clients'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/clients');
    if (error) throw problem(error, 'Could not load the clients.');
    return data;
  },
});

/** PRC-02 at v1.943: null puts the client back on the default group. */
export async function setClientPriceGroup(clientId: string, priceGroupId: string | null) {
  const { data, error } = await api.PATCH('/v1/clients/{id}/price-group', {
    params: { path: { id: clientId } },
    body: { priceGroupId },
  });
  if (error) throw problem(error, 'Could not change the price group.');
  return data;
}

export async function createClient(input: NewClient) {
  const { data, error } = await api.POST('/v1/clients', { body: input });
  // The API names the field that is wrong (422), so the message is shown as-is rather than
  // replaced with something vaguer.
  if (error) throw problem(error, 'Could not save that client.');
  return data;
}
